import { useQuery } from '@tanstack/react-query';
import { SyntheticEvent, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from 'src/components/Autocomplete';
import { Avatar } from 'src/components/Avatar';
import { Box } from 'src/components/Box';
import { Chip } from 'src/components/Chip';
import { ListItem } from 'src/components/ListItem';
import { InputAdornment, TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { debounce } from 'src/components/muiTheme/utils';
import { useSession } from 'src/contexts/SessionContext';
import { Recipient } from 'src/models/conversation/_types';
import {
  conversationQueryKeys,
  searchAvailableRecipients,
} from 'src/models/conversation/conversation';
import { User } from 'src/models/user/_types';
import { fetchTrainers } from 'src/models/user/user';
import { getUserDisplayName, isTrainer } from 'src/models/user/utils';

export type MessageRecipientScope = 'all' | 'trainers';

interface UserInputProps {
  onChange: (e: SyntheticEvent | undefined, newValue: Recipient[]) => void;
  /** Restrict picker options. `trainers` matches legacy notify-staff (platform trainers only). */
  recipientScope?: MessageRecipientScope;
  value: Recipient[];
}

const trainerToRecipient = (trainer: User): Recipient => ({
  id: trainer.id,
  itemType: 'Trainer',
  nickname: getUserDisplayName(trainer),
  profileImage: trainer.profileImage,
  type: 'Trainer',
});

export const UserInput: React.FC<UserInputProps> = ({
  onChange,
  recipientScope = 'all',
  value,
}) => {
  const [inputValue, setInputValue] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [isFocused, setIsFocused] = useState(false);
  const { user } = useSession();
  const { t: tCommon } = useTranslation('translation', { keyPrefix: 'common' });
  const trainersOnly = recipientScope === 'trainers';

  const debouncedSetSearchTerm = useMemo(
    () => debounce((value: string) => setDebouncedSearchTerm(value), 300),
    []
  );

  const handleInputChange = useCallback(
    (_event: any, newInputValue: string) => {
      setInputValue(newInputValue);
      debouncedSetSearchTerm(newInputValue);
    },
    [debouncedSetSearchTerm]
  );

  const { data: recipients, isLoading: recipientsLoading } = useQuery({
    queryKey: conversationQueryKeys.availableRecipients(debouncedSearchTerm),

    queryFn: async () => {
      const list = await searchAvailableRecipients({
        search: debouncedSearchTerm,
      });
      return list;
    },

    enabled: isFocused && !trainersOnly,
  });

  const { data: trainers, isLoading: trainersLoading } = useQuery({
    enabled: isFocused && trainersOnly,
    queryFn: fetchTrainers,
    queryKey: conversationQueryKeys.notifyStaffTrainers(),
    staleTime: 60_000,
  });

  const candidates = useMemo(() => {
    if (trainersOnly) {
      const term = debouncedSearchTerm.trim().toLowerCase();
      return (trainers ?? [])
        .map(trainerToRecipient)
        .filter(
          (candidate) =>
            !term || candidate.nickname.toLowerCase().includes(term)
        );
    }

    return (recipients || []).filter(
      (c) =>
        isTrainer(user) || !c.type || c.type === 'User' || c.type === 'Trainer'
    );
  }, [debouncedSearchTerm, recipients, trainers, trainersOnly, user]);

  const optionsLoading = trainersOnly ? trainersLoading : recipientsLoading;

  const noOptionsText = tCommon('messages.noOptions');

  const handleFocus = () => setIsFocused(true);
  const handleBlur = (event: React.FocusEvent) => {
    const relatedTarget = event.relatedTarget as HTMLElement;
    if (relatedTarget?.closest('.MuiChip-root')) {
      return;
    }
    setIsFocused(false);
  };

  const recipientString = useMemo(() => {
    if (value && value.length > 0) {
      if (value.length <= 3) {
        return value.map((r) => r.nickname).join(', ');
      } else {
        const visibleRecipients = value
          .slice(0, 3)
          .map((r) => r.nickname)
          .join(', ');
        return `${visibleRecipients} ${tCommon('pagination.andMore', {
          count: value.length - 3,
        })}`;
      }
    }
  }, [value, tCommon]);

  return (
    <Box mx={1}>
      <Autocomplete
        multiple
        disablePortal
        id="combo-box-demo"
        options={candidates}
        loading={optionsLoading}
        onChange={onChange}
        value={value}
        inputValue={inputValue}
        onInputChange={handleInputChange}
        getOptionLabel={(option) => option.nickname}
        renderOption={(props, option) => {
          return (
            <ListItem
              {...(props as any)}
              primary={
                <span dangerouslySetInnerHTML={{ __html: option.nickname }} />
              }
              secondary={
                option.aliases?.length ? option.aliases.join(', ') : ''
              }
            />
          );
        }}
        noOptionsText={noOptionsText}
        filterOptions={(x) => x}
        popupIcon={false}
        forcePopupIcon={false}
        sx={{
          width: '100%',
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            placeholder=""
            variant="standard"
            onFocus={handleFocus}
            onBlur={handleBlur}
            InputProps={{
              ...params.InputProps,
              disableUnderline: true,
              startAdornment: (
                <>
                  <InputAdornment position="start" sx={{ mr: 1 }}>
                    {tCommon('labels.to')}
                  </InputAdornment>
                  {isFocused ? (
                    params.InputProps.startAdornment
                  ) : (
                    <Typography component="span" sx={{ mr: 1 }}>
                      {recipientString}
                    </Typography>
                  )}
                </>
              ),
            }}
          />
        )}
        renderTags={(tagValue, getTagProps) =>
          isFocused
            ? tagValue.map((option, index) => {
                const { key: _tagKey, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    key={option.id}
                    {...tagProps}
                    variant="outlined"
                    label={
                      <span
                        dangerouslySetInnerHTML={{ __html: option.nickname }}
                      />
                    }
                    avatar={
                      option.profileImage ? (
                        <Avatar
                          size="xsmall"
                          src={option.profileImage}
                          sx={{ ml: 0.5 }}
                        />
                      ) : (
                        <Avatar size="xsmall" sx={{ ml: 0.5 }}>
                          {option.nickname[0].toUpperCase()}
                        </Avatar>
                      )
                    }
                  />
                );
              })
            : null
        }
      />
    </Box>
  );
};
